import type { Appointment } from "../../types/garage/planning";
import type { OrdreStatus } from "types/ordre";
import { formatTime } from "@utils/timeGrid";
import { STATUS_CHIP_STYLES } from "./dropdown/OrdreStatusDropdown";

/**
 * Black hover box shown over a planning appointment.
 * Displays client / plate / dates and, when the appointment is linked to an
 * ordre, a clickable status chip that opens the status menu (via onEditStatus).
 *
 * Rendered as a direct child of the appointment cell: positioning reads
 * `el.parentElement` to anchor itself beside that cell.
 */
export default function AppointmentTooltip({
  appt,
  appointments,
  status,
  fallbackDate,
  onEditStatus,
  onMouseEnter,
  onMouseLeave,
}: {
  appt: Appointment;
  appointments: Appointment[];
  status?: OrdreStatus;
  fallbackDate?: string;
  onEditStatus: (anchor: DOMRect) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const thisDate = appt.date || fallbackDate || "";
  const fmtDate = (d: string) =>
    new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

  const pair = appt.ordre_id ? appointments.find((a) => a.ordre_id === appt.ordre_id && a.id !== appt.id) : null;
  const receptionAppt = appt.type === "reception" ? appt : pair;
  const restitutionAppt = appt.type === "restitution" ? appt : pair;

  const chip = status ? STATUS_CHIP_STYLES[status] : null;

  return (
    <div
      data-appt-tooltip
      className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2"
      style={{ whiteSpace: "nowrap", minWidth: 140 }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={(el) => {
        if (!el) return;
        const parent = el.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const tipW = el.offsetWidth;
        // Place the tooltip flush against the event (1px overlap) instead of with a
        // gap: an 8px gap can sit over a neighbouring event, so moving the pointer
        // toward the tooltip would land on that event and steal/hide the hover.
        const fitsRight = rect.right + tipW <= window.innerWidth;
        el.style.top = `${rect.top}px`;
        el.style.left = fitsRight ? `${rect.right - 1}px` : `${rect.left - tipW + 1}px`;
      }}
    >
      <div className="text-h4 font-medium">{appt.clientName}</div>
      {appt.plate && <div className="text-h5 text-neutral-300">{appt.plate}</div>}
      {appt.num && <div className="text-h5 text-neutral-300">OR n°{appt.num}</div>}

      {receptionAppt && (
        <div className="text-h5 text-neutral-300">
          Réception : {fmtDate(receptionAppt.date || thisDate)} {formatTime(receptionAppt.startHour)} — {formatTime(receptionAppt.startHour + receptionAppt.durationHours)}
        </div>
      )}
      {restitutionAppt && (
        <div className="text-h5 text-neutral-300">
          Restitution : {fmtDate(restitutionAppt.date || thisDate)} {formatTime(restitutionAppt.startHour)} — {formatTime(restitutionAppt.startHour + restitutionAppt.durationHours)}
        </div>
      )}
      {!receptionAppt && !restitutionAppt && (
        <div className="text-h5 text-neutral-300">
          {fmtDate(thisDate)} {formatTime(appt.startHour)} — {formatTime(appt.startHour + appt.durationHours)}
        </div>
      )}

      {chip && status ? (
        <div className="flex flex-col gap-1 mt-2">
          <span className="text-h5 text-neutral-400">Statut de l'OR</span>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onEditStatus((e.currentTarget as HTMLElement).getBoundingClientRect());
            }}
            className="flex flex-row items-center gap-1.5 font-medium cursor-pointer transition-opacity hover:opacity-80 w-fit"
            style={{ background: chip.bg, color: chip.color, borderRadius: 5, padding: "3px 10px", fontSize: 12, lineHeight: "16px" }}
          >
            {chip.label}
            <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
              <path d="M1 1l3 3 3-3" stroke={chip.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="text-h5 text-neutral-400 mt-1">Cliquer pour ouvrir</div>
      )}
    </div>
  );
}
